import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { setSelfie, uploadSelfie, clearApiError } from '../store/slices/userSlice';
import TcButton from '../components/TcButton';
import TcPaper from '../components/TcPaper';
import ErrorAlert from '../components/shared/ErrorAlert';
import { compressImage, getDataUrlSizeKB, checkVisitorIdSync, getAllCookies } from '../utils/functions';
import { useScrollToBottom } from '../hooks/useScrollToBottom';
import { useDeviceDetection } from '../hooks/useDeviceDetection';

export default function SelfieCapture() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { selfieUrl, visitorId, isLoading, apiError, name, email, ageGroup, nationality } = useAppSelector(state => state.user);
    const { orientation } = useDeviceDetection();
    const isLandscape = orientation === 'landscape';
    
    // Debug Redux state and visitor ID sync
    console.log('🔍 SelfieCapture Redux state:', {
        visitorId,
        name,
        email,
        ageGroup,
        nationality,
        isLoading,
        apiError
    });
    
    // Check for visitor ID sync issues
    if (visitorId) {
        const syncCheck = checkVisitorIdSync(visitorId);
        console.log('🔄 Visitor ID sync check:', syncCheck);
        
        if (syncCheck.needsSync) {
            console.warn('⚠️ Visitor ID mismatch in SelfieCapture:', {
                reduxVisitorId: visitorId,
                cookieVisitorId: syncCheck.cookieVisitorId,
                allCookies: getAllCookies()
            });
        }
    } else {
        console.log('🍪 All cookies for debugging:', getAllCookies());
    }
    
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [photo, setPhoto] = useState<string | null>(selfieUrl || null);
    const [cameraActive, setCameraActive] = useState(false);
    const [cameraError, setCameraError] = useState(false);
    const [uploadInProgress, setUploadInProgress] = useState(false);

    // Use the custom hook for scroll detection
    const isAtBottom = useScrollToBottom({
        containerSelector: '.main-content-wrapper'
    });
    
    // Camera manager for better cleanup
    const cameraManagerRef = useRef<{
        stream: MediaStream | null;
        destroy: () => void;
    } | null>(null);

    // Function to stop camera stream (moved outside useEffect for better reliability)
    const stopCamera = () => {
        if (cameraManagerRef.current) {
            cameraManagerRef.current.destroy();
            cameraManagerRef.current = null;
        }
        
        // Additional cleanup: Ensure video element is completely cleared
        const videoElement = videoRef.current;
        if (videoElement) {
            // Pause video first
            videoElement.pause();
            
            // Clear all possible sources
            videoElement.srcObject = null;
            videoElement.src = '';
            
            // Force reload to release any remaining references
            videoElement.load();
            
            // Remove any event listeners that might hold references
            videoElement.onloadedmetadata = null;
            videoElement.onplay = null;
        }
        
        setCameraActive(false);
    };

    // Start the camera when component mounts
    useEffect(() => {
        startCamera();

        return () => {
            // Cleanup camera stream
            stopCamera();
        };
    }, []);

    // Clear API error when component unmounts
    useEffect(() => {
        return () => {
            dispatch(clearApiError());
        };
    }, [dispatch]);
    
    const startCamera = async () => {
        try {
            setCameraError(false);
            
            // Stop any existing camera first
            if (cameraManagerRef.current) {
                cameraManagerRef.current.destroy();
            }
            
            // Record timestamp when camera is accessed for cleanup tracking
            localStorage.setItem('lastCameraAccess', Date.now().toString());
            
            const stream = await navigator.mediaDevices.getUserMedia({ 
                video: { facingMode: 'user' } 
            });
            
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                setCameraActive(true);
                
                // Create camera manager with destroy method
                cameraManagerRef.current = {
                    stream,
                    destroy: () => {
                        // Get all tracks and stop them aggressively
                        const tracks = stream.getTracks();
                        tracks.forEach(track => {
                            track.stop();
                            // Force track to end state
                            if (track.readyState !== 'ended') {
                                console.warn('Track not properly ended, forcing stop');
                            }
                        });
                        
                        // Clear video element immediately
                        if (videoRef.current) {
                            videoRef.current.pause();
                            videoRef.current.srcObject = null;
                            videoRef.current.src = '';
                            videoRef.current.load();
                        }
                        
                        // Nullify stream reference
                        try {
                            // Additional cleanup for stubborn browsers
                            if (stream.active) {
                                console.warn('Stream still active after stopping tracks');
                            }
                        } catch (e) {
                            // Stream might already be disposed
                        }
                    }
                };
            }
        } catch (err) {
            console.error("Error accessing camera:", err);
            setCameraError(true);
        }
    };
    
    const captureSelfie = async () => {
        if (!videoRef.current) return;
        
        // Create a canvas element to capture the video frame
        const canvas = document.createElement('canvas');
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        
        // Draw the video frame to the canvas
        const ctx = canvas.getContext('2d');
        if (ctx) {
            // Flip horizontally to mirror the image
            ctx.translate(canvas.width, 0);
            ctx.scale(-1, 1);
            ctx.drawImage(videoRef.current, 0, 0);
        }
        
        // Stop the camera after capturing the frame
        stopCamera();

        let dataUrl: string = '';
        try {
            // Compress the image to ensure it's under 5MB
            dataUrl = await compressImage(canvas, 5120); // 5120KB = 5MB
            const fileSizeKB = getDataUrlSizeKB(dataUrl);
            console.log(`Selfie captured and compressed to ${Math.round(fileSizeKB)}KB`);
        } catch (error) {
            console.error('Error compressing selfie:', error);
            // Fallback to JPEG format with default quality if compression fails
            dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        } finally {
            // Save to localStorage and Redux
            setPhoto(dataUrl);
            localStorage.setItem('selfie', dataUrl);
            dispatch(setSelfie(dataUrl));
        }
    };
    
    const handleRetake = () => {
        setPhoto(null);
        // Clear any API errors
        if (apiError) {
            dispatch(clearApiError());
        }
        startCamera();
    };
    
    const handleContinue = async () => {
        // Ensure camera is stopped before continuing
        stopCamera();
        
        // If we have a visitor ID and photo, upload to API
        if (visitorId && photo && !uploadInProgress) {
            setUploadInProgress(true);
            
            console.log('🔍 Debug selfie upload - visitorId from Redux:', {
                visitorId,
                visitorIdType: typeof visitorId,
                visitorIdLength: visitorId?.length,
                hasPhoto: !!photo,
                photoLength: photo?.length
            });
            
            try {
                const result = await dispatch(uploadSelfie({
                    visitorId,
                    selfieDataUrl: photo,
                    filename: `selfie_${visitorId}_${Date.now()}.jpg`
                }));
                
                if (uploadSelfie.fulfilled.match(result)) {
                    // Successfully uploaded, navigate to next screen
                    console.log('✅ Selfie uploaded successfully');
                    navigate('/ready-to-explore');
                } else {
                    // Upload failed, but we can still continue
                    // The error will be shown in the UI
                    console.warn('⚠️ Selfie upload failed, but user can continue');
                    setUploadInProgress(false);
                    
                    // Show error message to user (they can still continue)
                    // The error is automatically displayed via Redux state
                }
            } catch (error) {
                console.error('Unexpected error during selfie upload:', error);
                setUploadInProgress(false);
            }
        } else {
            // No visitor ID or photo, just continue
            navigate('/ready-to-explore');
        }
    };

    const handleSkip = () => {
        // Ensure camera is stopped before skipping
        stopCamera();
        navigate('/ready-to-explore');
    };

    const renderContent = () => {
        if (cameraError) {
            return (
                <>
                    <div className="error-message mx-8 my-6 max-w-sm">
                        <p dangerouslySetInnerHTML={{ __html: t('selfieCapture.cameraError') }} />
                    </div>
                    
                    <div className={`button-wrapper stick-to-bottom flex flex-col gap-4 p-4 !bottom-[4vw] !max-w-[92vw] rounded-lg has-transition ${
                        isAtBottom ? 'clear-transparent' : 'glossy-translucent-black-with-border'
                    }`}>
                        <TcButton
                            handleClick={startCamera}
                            primary
                            classNameStyle="mt-4"
                        >
                            {t('common.tryAgain')}
                        </TcButton>
                        
                        <button
                            onClick={handleSkip}
                            className="text-white hover:text-gray-200 py-2 text-base"
                            disabled={isLoading || uploadInProgress}
                        >
                            {t('common.skip')}
                        </button>
                    </div>
                </>
            );
        }

        if (photo) {
            return (
                <div className="pb-0 max-w-lg mx-auto">
                    <TcPaper classNameStyle="mb-4">
                        <h1 className="main-content-title"
                            dangerouslySetInnerHTML={{ __html: t('selfieCapture.lookingGood') }}
                        />
                    </TcPaper>

                    {/* API Error Display */}
                    {apiError && (
                        <div className="px-8 max-w-lg mx-auto mb-4">
                            <ErrorAlert 
                                message={apiError}
                                    onDismiss={() => dispatch(clearApiError())}
                                />
                        </div>
                    )}

                    <div className="relative max-w-[288px] max-h-[288px] mx-auto mb-4">
                        <img
                            src={photo}
                            alt={t('selfieCapture.capturedSelfie')}
                            className="w-72 h-72 mx-auto object-cover rounded-full border-2 border-blue-500"
                        />
                    </div>

                    <TcPaper classNameStyle="mt-6">
                        <p
                            dangerouslySetInnerHTML={{ __html: t('selfieCapture.captureSelfieDescription') }}
                        />
                    </TcPaper>

                    <div className={`button-wrapper stick-to-bottom flex flex-col gap-4 p-4 !bottom-[4vw] !max-w-[92vw] rounded-lg has-transition ${
                        isAtBottom ? 'clear-transparent' : 'glossy-translucent-black-with-border'
                    }`}>
                        <TcButton 
                            handleClick={handleRetake}
                            disabled={isLoading || uploadInProgress}
                        >
                            {t('selfieCapture.retake')}
                        </TcButton>

                        <TcButton 
                            handleClick={handleContinue} 
                            primary
                            disabled={isLoading || uploadInProgress}
                        >
                            {(isLoading || uploadInProgress) ? (
                                <div className="flex items-center gap-2">
                                    <Loader2 size={16} className="animate-spin" />
                                    {uploadInProgress ? t('selfieCapture.uploading') : t('common.loading')}
                                </div>
                            ) : (
                                t('common.continue')
                            )}
                        </TcButton>
                    </div>
                </div>
            );
        }

        // Default: camera view
        return (
            <div className="pb-0 max-w-lg mx-auto">
                {/* API Error Display */}
                {apiError && (
                    <ErrorAlert 
                        message={apiError}
                        onDismiss={() => dispatch(clearApiError())}
                    />
                )}

                <div className="relative max-w-[288px] max-h-[288px] mx-auto mt-4 mb-4 rounded-full overflow-hidden border-2 border-blue-500">
                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        className="w-72 h-72 mx-auto object-cover mirror-mode"
                        style={{ transform: 'scaleX(-1)' }}
                    />
                </div>

                {cameraActive && (
                    <TcPaper classNameStyle="mt-6">
                        <h1 className="main-content-title">
                            {t('selfieCapture.sayCheese')}
                        </h1>
                        <p className="mt-4"
                            dangerouslySetInnerHTML={{ __html: t('selfieCapture.description') }}
                        />
                    </TcPaper>
                )}

                {cameraActive && (
                    <div className={`button-wrapper stick-to-bottom flex flex-col gap-4 p-4 !bottom-[4vw] !max-w-[92vw] has-transition ${
                        isAtBottom ? 'clear-transparent' : 'glossy-translucent-black-with-border'
                    }`}>
                        <TcButton
                            handleClick={captureSelfie}
                            primary
                            disabled={isLoading}
                        >
                            {t('selfieCapture.captureSelfie')}
                        </TcButton>

                        <button
                            onClick={handleSkip}
                            className="text-white hover:text-gray-200 py-2 text-base"
                            disabled={isLoading || uploadInProgress}
                        >
                            {t('common.skip')}
                        </button>
                    </div>
                )}
            </div>
        );
    };
    
    return (
        <div className={`
            main-content-wrapper 
            ${isLandscape ? '!pt-8' : '!pt-[112px]'} 
            unselectable 
            has-transition 
        `}>
            {renderContent()}
        </div>
    );
}